import { useState, useEffect, useCallback, useMemo } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Plus, FileText, Search, Loader2, AlertTriangle, ArrowUpDown, Settings2, Layers } from 'lucide-react';
import * as api from '../lib/esignApi';
import { useAuth } from '../hooks/useAuth';
import StatusFilterRail from '../components/esign/StatusFilterRail';
import EnvelopeRow from '../components/esign/EnvelopeRow';
import SignInRequired from '../components/SignInRequired';

const SORT_OPTIONS = [
  { key: 'activity', label: 'Last activity' },
  { key: 'created', label: 'Created' },
  { key: 'subject', label: 'Subject' },
  { key: 'status', label: 'Status' },
];

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function EsignDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('activity');
  const [dir, setDir] = useState('desc');
  const [actionState, setActionState] = useState(null); // { envelope, kind }
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const debouncedSearch = useDebounced(search, 300);

  const load = useCallback(
    async (cursor = null) => {
      try {
        const data = await api.listEnvelopes({
          status: statusFilter || undefined,
          q: debouncedSearch || undefined,
          sort,
          dir,
          limit: 20,
          cursor,
          counts: !cursor, // only request counts on first page
        });
        if (cursor) {
          setItems((prev) => [...prev, ...(data.items || [])]);
        } else {
          setItems(data.items || []);
          if (data.status_counts) setCounts(data.status_counts);
        }
        setNextCursor(data.next_cursor || null);
        setError(null);
      } catch (err) {
        setError(err.message);
      }
    },
    [statusFilter, debouncedSearch, sort, dir]
  );

  useEffect(() => {
    // Wait for Firebase Auth to hydrate before calling the API —
    // otherwise getIdToken() throws "Not signed in" on first paint.
    if (authLoading || !user) return;
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load, authLoading, user]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await load(nextCursor);
    setLoadingMore(false);
  }, [nextCursor, loadingMore, load]);

  const handleAction = useCallback(
    (envelope, kind) => {
      if (kind === 'view') {
        navigate(`/esign/${envelope.id}`);
        return;
      }
      if (kind === 'void' || kind === 'delete') {
        setActionState({ envelope, kind });
        return;
      }
      // Fire-and-confirm actions
      setActionBusy(true);
      (async () => {
        try {
          if (kind === 'duplicate') {
            const result = await api.duplicateEnvelope(envelope.id);
            setToast({ kind: 'success', text: 'Duplicate created. Opening…' });
            navigate(`/esign/${result.id}`);
          } else if (kind === 'download-signed') {
            const { url } = await api.getCompletedUrl(envelope.id);
            window.open(url, '_blank');
          } else if (kind === 'download-cert') {
            const { url } = await api.getCertificateUrl(envelope.id);
            window.open(url, '_blank');
          } else if (kind === 'resend-all') {
            const pending = (envelope.recipients || []).filter((r) =>
              ['pending', 'sent', 'viewed'].includes(r.status) && r.role === 'signer'
            );
            let sent = 0;
            let throttled = 0;
            for (const r of pending) {
              try {
                await api.resendInvite(envelope.id, r.id);
                sent += 1;
              } catch (err) {
                if (err.status === 429) throttled += 1;
              }
            }
            if (sent > 0) setToast({ kind: 'success', text: `Reminder sent to ${sent} recipient${sent !== 1 ? 's' : ''}.` });
            else if (throttled > 0) setToast({ kind: 'warn', text: 'Already reminded everyone recently.' });
          }
        } catch (err) {
          setToast({ kind: 'error', text: err.message });
        } finally {
          setActionBusy(false);
        }
      })();
    },
    [navigate]
  );

  const confirmAction = useCallback(async () => {
    if (!actionState) return;
    setActionBusy(true);
    try {
      if (actionState.kind === 'void') {
        await api.voidEnvelope(actionState.envelope.id);
        setToast({ kind: 'success', text: 'Envelope voided.' });
      } else if (actionState.kind === 'delete') {
        await api.deleteEnvelope(actionState.envelope.id);
        setToast({ kind: 'success', text: 'Draft deleted.' });
      }
      setActionState(null);
      await load();
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    } finally {
      setActionBusy(false);
    }
  }, [actionState, load]);

  const toggleDir = () => setDir((d) => (d === 'desc' ? 'asc' : 'desc'));

  const filteredEmptyHint = useMemo(() => {
    if (debouncedSearch) return `No envelopes match "${debouncedSearch}".`;
    if (statusFilter) return `No envelopes in this bucket yet.`;
    return null;
  }, [debouncedSearch, statusFilter]);

  // Logged-out (e.g. a licensed desktop user who signed out): eSign needs a
  // live session — prompt sign-in instead of a dead page.
  if (!authLoading && !user) {
    return <SignInRequired feature="eSign" />;
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">eSign</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/esign/templates')}
            className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5 text-sm"
          >
            <Layers className="w-4 h-4" />
            Templates
          </button>
          <button
            onClick={() => navigate('/account/esign-settings')}
            className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5 text-sm"
          >
            <Settings2 className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={() => navigate('/esign/new')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Envelope
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm border ${
            toast.kind === 'error'
              ? 'bg-red-50 border-red-200 text-red-700'
              : toast.kind === 'warn'
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          {toast.text}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <StatusFilterRail active={statusFilter} counts={counts} onChange={setStatusFilter} />

        <div className="flex-1 min-w-0">
          {/* Search + sort */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by subject or signer email…"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  Sort: {o.label}
                </option>
              ))}
            </select>
            <button
              onClick={toggleDir}
              className="border border-gray-200 rounded-lg px-2 py-2 text-gray-500 hover:bg-gray-50"
              title={`Sort ${dir === 'desc' ? 'descending' : 'ascending'}`}
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-700 mb-1">
                {filteredEmptyHint || 'No envelopes yet'}
              </h2>
              {!filteredEmptyHint && (
                <>
                  <p className="text-sm text-gray-500 mb-6">
                    Send your first document for electronic signature.
                  </p>
                  <button
                    onClick={() => navigate('/esign/new')}
                    className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition"
                  >
                    Create your first envelope
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Subject</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Recipients</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Last activity</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((env) => (
                      <EnvelopeRow
                        key={env.id}
                        envelope={env}
                        onClick={() => navigate(`/esign/${env.id}`)}
                        onAction={(kind) => handleAction(env, kind)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {nextCursor && (
                <div className="p-4 border-t border-gray-100 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2 justify-center">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Loading…
                      </span>
                    ) : (
                      'Load more'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirm modal for void/delete */}
      {actionState && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {actionState.kind === 'void' ? 'Void envelope?' : 'Delete draft?'}
                </h3>
                <p className="text-sm text-gray-600">
                  {actionState.kind === 'void'
                    ? 'This cancels all pending signatures. Recipients will be notified. This action cannot be undone.'
                    : 'This permanently deletes the draft envelope.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setActionState(null)}
                disabled={actionBusy}
                className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={actionBusy}
                className={`${
                  actionState.kind === 'void' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'
                } text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50`}
              >
                {actionBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {actionState.kind === 'void' ? 'Yes, void' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
